import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "react-query";
import { apiV2 } from "../../../api/axiosConfig";
import { Client, Department } from "../../../types/userManagement";
import { PaginatedResponse } from "../../../types/PaginatedResponse";
import { DeviceDataTypes } from "../../../types/device-data-types";
import { useDebounce } from "use-debounce";
import { useAuth } from "../../../context/user";
import { useUserManagementBrowsing } from "../../../lib/user-management/UserManagementBrowsingContext";
import { fetchDevices } from "./useDeviceExport"; 

interface DevicesManagementContextType {
    client?: Client;
    setClient: (client?: Client) => void;
    departments?: Department[];
    setDepartments: (departments: Department[]) => void;
    devices?: PaginatedResponse<DeviceDataTypes>;
    isLoading: boolean;
    error: any;
    selected: Set<string>;
    addSelected: (id: string) => void;
    removeSelected: (id: string) => void;
    selectAll: () => void;
    deselectAll: () => void;
    page: number;
    setPage: (page: number) => void;
    limit: number;
    refetch: () => void;
    actionError: string;
    setActionError: (error: string) => void;
    archived: boolean;
    setArchived: (archived: boolean) => void;
    search: string;
    setSearch: (search: string) => void;
    selectAllPages: () => void;
    archiveMultiple: () => Promise<void>;
    unarchiveMultiple: () => Promise<void>;
    submitting: boolean;
    setSubmitting: (submitting: boolean) => void;
}

const DevicesManagementContext = createContext<DevicesManagementContextType | undefined>(undefined);

interface DevicesManagementProviderProps {
    children: ReactNode;
}

export const DevicesManagementProvider: React.FC<DevicesManagementProviderProps> = ({ children }) => {
    const browsingCtx = useUserManagementBrowsing(); // you may rename this if needed
    const { searchParams, setSearchParams } = browsingCtx;

    const [client, setClient] = useState<Client>();
    const [departments, setDepartments] = useState<Department[]>([]);
    const [selected, setSelected] = useState(new Set<string>());
    const [page, setPage] = useState<number>(Number(searchParams.get("page")) || 1);
    const [limit, setLimit] = useState<number>(10);
    const [archived, setArchived] = useState(false);
    const [search, setSearch] = useState(searchParams.get("search") || "");
    const [debouncedSearch] = useDebounce(search, 300);
    const [submitting, setSubmitting] = useState(false);
    const [actionError, setActionError] = useState("");

    useEffect(() => setPage(1), [debouncedSearch]);
    useEffect(() => browsingCtx.setClientID(client?.id || ""), [client]);
    useEffect(() => {
        if (actionError !== "") {
            setTimeout(() => setActionError(""), 10000);
        }
    }, [actionError]);

    const addSelected = (id: string) => setSelected(prev => new Set(prev).add(id));
    const removeSelected = (id: string) => {
        const newSet = new Set(selected);
        newSet.delete(id);
        setSelected(newSet);
    };
    const selectAll = () => {
        if (devices) {
            const newSet = new Set(selected);
            devices.items.forEach(d => d.id && newSet.add(d.id));
            setSelected(newSet);
        }
    };
    const deselectAll = () => setSelected(new Set());

    const selectAllPages = async () => {
        let page = 1;
        const ids: string[] = [];
        while (true) {
            const devices = await fetchDevices(client?.id || "", page, 50, archived, departments.map(d => d.id), debouncedSearch);
            if (devices?.items.length === 0) break;
            page += 1;
            devices.items.forEach(d => d.id && ids.push(d.id));
        }
        setSelected(new Set(ids));
    };

    const auth = useAuth();

    const enableQuery = useMemo(() => {
        return auth.isSuperAdmin ? !!client : true;
    }, [auth.isSuperAdmin, client]);

    const { data: devices, isLoading, error, refetch } = useQuery(
        ["devices", client, departments, archived, page, limit, debouncedSearch],
        () => fetchDevices(client?.id || "", page, limit, archived, departments.map(d => d.id), debouncedSearch),
        { enabled: enableQuery }
    );

    useEffect(() => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev.toString());
            newParams.set("page", `${page}`);
            newParams.set("search", `${debouncedSearch}`);
            return newParams;
        });
    }, [debouncedSearch, page]);

    const archiveMultiple = useCallback(async () => {
        await apiV2.patch("devices/archive-multiple", { ids: Array.from(selected) });
        deselectAll();
    }, [selected]);

    const unarchiveMultiple = useCallback(async () => {
        await apiV2.patch("devices/unarchive-multiple", { ids: Array.from(selected) });
        deselectAll();
    }, [selected]);

    return (
        <DevicesManagementContext.Provider value={{
            client,
            setClient,
            departments,
            setDepartments,
            devices,
            isLoading,
            error,
            addSelected,
            removeSelected,
            selectAll,
            deselectAll,
            selected,
            page,
            limit,
            setPage,
            refetch,
            actionError,
            setActionError,
            archived,
            setArchived,
            search,
            setSearch,
            selectAllPages,
            archiveMultiple,
            unarchiveMultiple,
            submitting,
            setSubmitting,
        }}>
            {children}
        </DevicesManagementContext.Provider>
    );
};

export const useDevicesManagement = (): DevicesManagementContextType => {
    const context = useContext(DevicesManagementContext);
    if (!context) {
        throw new Error("useDevicesManagement must be used within a DevicesManagementProvider");
    }
    return context;
};
